import React from 'react';
import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiSearchColumns,
  dvtSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import { AccountModel, BoPhan, ChiPhi, DonViTinh, HopDong, KheUoc, Phi, Tax, VuViec } from '@/types/schemas';
import { ServiceFormFieldActions, ServiceFormFieldState } from '../../hooks';
import { FormField, SearchField } from '@/components/custom/erp';
import { FormMode } from '@/types/form';

interface ServiceFormProps {
  formMode: FormMode;
  serviceState: {
    state: ServiceFormFieldState;
    actions: ServiceFormFieldActions;
  };
}

export default function ServiceForm({ formMode, serviceState: { state, actions } }: ServiceFormProps) {
  const isDisabled = formMode === 'view';
  const rowClassName = 'flex min-w-0 items-start';
  const labelClassName = 'w-[180px] shrink-0 pr-2 pt-2';
  const fieldClassName = 'min-w-0 flex-1';
  const lookupFieldClassName = 'w-full max-w-[360px]';
  const compactFieldClassName = 'w-full max-w-[320px]';
  const fullFieldClassName = 'w-full max-w-full';

  return (
    <div className='min-w-0 max-w-full overflow-x-hidden p-4'>
      <div className='space-y-1'>
        {/* Service Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã dịch vụ</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='ma_dv'
              type='text'
              className={compactFieldClassName}
              disabled={isDisabled || formMode === 'edit'}
            />
          </div>
        </div>

        {/* Service Name */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Tên dịch vụ</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='ten_dv'
              type='text'
              className={fullFieldClassName}
              inputClassName='w-full'
              disabled={isDisabled}
            />
          </div>
        </div>

        {/* Alternative Name */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Tên khác</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='ten_dv2'
              type='text'
              className={fullFieldClassName}
              inputClassName='w-full'
              disabled={isDisabled}
            />
          </div>
        </div>

        {/* Unit */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Đvt</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<DonViTinh>
              type='text'
              columnDisplay='dvt'
              displayRelatedField='ten_dvt'
              searchEndpoint={`/${QUERY_KEYS.DON_VI_TINH}/`}
              searchColumns={dvtSearchColumns}
              dialogTitle='Danh mục đơn vị tính'
              value={state.dvt?.dvt || ''}
              onRowSelection={actions.setDvt}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Revenue Account */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>TK doanh thu</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<AccountModel>
              type='text'
              columnDisplay='code'
              displayRelatedField='name'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              dialogTitle='Danh mục tài khoản'
              value={state.tkDoanhThu?.code || ''}
              onRowSelection={actions.setTkDoanhThu}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Cost Account */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>TK chi phí</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<AccountModel>
              type='text'
              columnDisplay='code'
              displayRelatedField='name'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              dialogTitle='Danh mục tài khoản'
              value={state.tkChiPhi?.code || ''}
              onRowSelection={actions.setTkChiPhi}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Tax Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã thuế</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<Tax>
              type='text'
              columnDisplay='ma_thue'
              displayRelatedField='ten_thue'
              searchEndpoint={`/${QUERY_KEYS.THUE}/`}
              searchColumns={thueSearchColumns}
              dialogTitle='Danh mục thuế'
              value={state.maThue?.ma_thue || ''}
              onRowSelection={actions.setMaThue}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Sell Price */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Giá bán</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='gia_nt2'
              type='number'
              className={compactFieldClassName}
              inputClassName='w-full'
              disabled={isDisabled}
              defaultValue={0.0}
            />
          </div>
        </div>

        {/* Purchase Price */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Giá mua</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='gia_nt'
              type='number'
              className={compactFieldClassName}
              inputClassName='w-full'
              disabled={isDisabled}
              defaultValue={0.0}
            />
          </div>
        </div>

        {/* Cost Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã chi phí</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<ChiPhi>
              type='text'
              columnDisplay='ma_cp'
              displayRelatedField='ten_cp'
              searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
              searchColumns={chiPhiSearchColumns}
              dialogTitle='Danh mục chi phí'
              value={state.maChiPhi?.ma_cp || ''}
              onRowSelection={actions.setMaChiPhi}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Fee Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã phí</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<Phi>
              type='text'
              columnDisplay='ma_phi'
              displayRelatedField='ten_phi'
              searchEndpoint={`/${QUERY_KEYS.PHI}/`}
              searchColumns={phiSearchColumns}
              dialogTitle='Danh mục phí'
              value={state.maPhi?.ma_phi || ''}
              onRowSelection={actions.setMaPhi}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Department Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã bộ phận</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<BoPhan>
              type='text'
              columnDisplay='ma_bp'
              displayRelatedField='ten_bp'
              searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
              searchColumns={boPhanSearchColumns}
              dialogTitle='Danh mục bộ phận'
              value={state.maBoPhan?.ma_bp || ''}
              onRowSelection={actions.setMaBoPhan}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Contract Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã hợp đồng</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<HopDong>
              type='text'
              columnDisplay='ma_hd'
              displayRelatedField='ten_hd'
              searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
              searchColumns={hopDongSearchColumns}
              dialogTitle='Danh mục hợp đồng'
              value={state.maHopDong?.ma_hd || ''}
              onRowSelection={actions.setMaHopDong}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Agreement */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Khế ước</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<KheUoc>
              type='text'
              columnDisplay='ma_ku'
              displayRelatedField='ten_ku'
              searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
              searchColumns={kheUocSearchColumns}
              dialogTitle='Danh mục khế ước'
              value={state.kheUoc?.ma_ku || ''}
              onRowSelection={actions.setKheUoc}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Case Code */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Mã vụ việc</span>
          </div>
          <div className={`flex items-center ${fieldClassName}`}>
            <SearchField<VuViec>
              type='text'
              columnDisplay='ma_vu_viec'
              displayRelatedField='ten_vu_viec'
              searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
              searchColumns={vuViecSearchColumns}
              dialogTitle='Danh mục vụ việc'
              value={state.maVuViec?.ma_vu_viec || ''}
              onRowSelection={actions.setMaVuViec}
              disabled={isDisabled}
              className={lookupFieldClassName}
            />
          </div>
        </div>

        {/* Status */}
        <div className={rowClassName}>
          <div className={labelClassName}>
            <span className='text-sm font-medium'>Trạng thái</span>
          </div>
          <div className={fieldClassName}>
            <FormField
              name='status'
              type='select'
              options={[
                { label: '1. Còn sử dụng', value: 1 },
                { label: '0. Không sử dụng', value: 0 }
              ]}
              defaultValue={1}
              className='w-full max-w-[420px]'
              inputClassName='w-full'
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
